import { Button } from "@/modules/shared/ui/components/button"
import {
  FaMapMarkerAlt,
  FaUniversity,
  FaUpload,
  FaCheckCircle,
  FaTimes,
  FaPersonBooth,
  FaExclamationCircle,
} from "react-icons/fa"
import { FileText } from "lucide-react"
import {
  EmployeeOnboardingDetailsDto,
  DocumentToBeSignedDto,
} from "../../employee/dto/employeeDetails"

interface EmployeeOnboardingDocumentsDto {
  requiredDocuments: string[]
  documentsToBeSigned: DocumentToBeSignedDto[]
}

interface ReviewSubmitFormProps {
  formData: EmployeeOnboardingDetailsDto
  response: string | null
  onClearResponse: () => void
  requiredDocFiles?: Map<string, File>
  signedDocFiles?: Map<string, File>
  onboardingDocs?: EmployeeOnboardingDocumentsDto | null
  isReadOnly?: boolean
}

interface FieldDef {
  label: string
  value: string | null | undefined
  required: boolean
}

function isEmpty(value: string | null | undefined) {
  return !value || value.trim() === ""
}

function FieldRow({ label, value, required }: FieldDef) {
  if (!isEmpty(value)) {
    return (
      <div className="flex justify-between items-start gap-2 py-1 border-b border-border/50 last:border-0">
        <span className="text-xs text-muted-foreground shrink-0">{label}</span>
        <span className="text-xs font-medium text-foreground text-right break-all">
          {value}
        </span>
      </div>
    )
  }

  if (required) {
    return (
      <div className="flex justify-between items-center gap-2 py-1 border-b border-border/50 last:border-0">
        <span className="text-xs text-muted-foreground shrink-0">{label}</span>
        <span className="flex items-center gap-1 text-xs font-semibold text-destructive">
          <FaExclamationCircle className="w-3 h-3 shrink-0" />
          Missing
        </span>
      </div>
    )
  }

  return (
    <div className="flex justify-between items-center gap-2 py-1 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs italic text-muted-foreground">Not provided</span>
    </div>
  )
}

function SectionHeader({
  icon,
  title,
  fields,
}: {
  icon: React.ReactNode
  title: string
  fields: FieldDef[]
}) {
  const missingRequired = fields.filter(
    (f) => f.required && isEmpty(f.value),
  ).length

  const gradientClass =
    missingRequired > 0
      ? "from-primary/5 to-primary/10"
      : "from-green-500/5 to-green-500/10"
  const borderClass =
    missingRequired > 0 ? "border-primary/20" : "border-green-200/50 dark:border-green-900/30"

  return (
    <div
      className={`bg-linear-to-br ${gradientClass} p-4 rounded-lg border ${borderClass}`}
    >
      <h3 className="font-semibold text-foreground mb-3 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2">
          {icon}
          {title}
        </span>
        {missingRequired > 0 && (
          <span className="text-xs font-semibold bg-destructive/10 text-destructive rounded-full px-2 py-0.5">
            {missingRequired} missing
          </span>
        )}
      </h3>
      <div className="space-y-0">
        {fields.map((f) => (
          <FieldRow key={f.label} {...f} />
        ))}
      </div>
    </div>
  )
}

export function ReviewSubmitForm({
  formData,
  response,
  onClearResponse,
  requiredDocFiles,
  signedDocFiles,
  onboardingDocs,
}: ReviewSubmitFormProps) {
  const employeeFields: FieldDef[] = [
    { label: "First Name", value: formData.firstName, required: true },
    { label: "Second Name", value: formData.secondName, required: true },
    { label: "Last Name", value: formData.lastName, required: true },
    { label: "Email", value: formData.email, required: true },
    { label: "Phone", value: formData.phone, required: true },
    { label: "Date of Birth", value: formData.dateOfBirth, required: true },
    { label: "Gender", value: formData.gender, required: true },
    { label: "Position", value: formData.position, required: true },
    { label: "ID Type", value: formData.identityType, required: true },
    { label: "ID Number", value: formData.identityNumber, required: true },
    { label: "Tax Number", value: formData.taxNumber, required: true },
    { label: "Race Group", value: formData.raceGroup, required: true },
    { label: "Marital Status", value: formData.maritalStatus, required: true },
    {
      label: "Emergency Contact",
      value: formData.emergencyContactName,
      required: false,
    },
    {
      label: "Emergency Phone",
      value: formData.emergencyContactPhone,
      required: false,
    },
  ]

  const addressFields: FieldDef[] = [
    { label: "Address Line 1", value: formData.address?.line1, required: true },
    {
      label: "Address Line 2",
      value: formData.address?.line2,
      required: false,
    },
    { label: "City", value: formData.address?.city, required: true },
    { label: "State", value: formData.address?.state, required: true },
    {
      label: "Postal Code",
      value: formData.address?.postalCode,
      required: true,
    },
    { label: "Country", value: formData.address?.country, required: true },
    {
      label: "Complex / Estate Name",
      value: formData.address?.complexName,
      required: false,
    },
    {
      label: "Unit / Complex Number",
      value: formData.address?.complexNumber,
      required: false,
    },
  ]

  const bankFields: FieldDef[] = [
    {
      label: "Account Holder",
      value: formData.bankDetails?.accountHolderName,
      required: true,
    },
    {
      label: "Bank Name",
      value: formData.bankDetails?.bankName,
      required: true,
    },
    {
      label: "Account Number",
      value: formData.bankDetails?.accountNumber
        ? `**** **** **** ${formData.bankDetails.accountNumber.slice(-4)}`
        : null,
      required: true,
    },
    {
      label: "Branch Code",
      value: formData.bankDetails?.branchCode,
      required: true,
    },
    {
      label: "Account Type",
      value: formData.bankDetails?.accountType,
      required: true,
    },
    {
      label: "Account Relationship",
      value: formData.bankDetails?.accountRelationship,
      required: true,
    },
  ]

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Employee Details */}
        <SectionHeader
          icon={<FaPersonBooth className="text-primary" />}
          title="Employee Details"
          fields={employeeFields}
        />

        {/* Address Details */}
        <SectionHeader
          icon={<FaMapMarkerAlt className="text-primary" />}
          title="Address Details"
          fields={addressFields}
        />

        {/* Bank Details */}
        <SectionHeader
          icon={<FaUniversity className="text-primary" />}
          title="Bank Details"
          fields={bankFields}
        />

        {/* Documents Status */}
        {(() => {
          const allRequiredUploaded =
            !onboardingDocs ||
            onboardingDocs.requiredDocuments.length === 0 ||
            onboardingDocs.requiredDocuments.every(
              (docType) =>
                requiredDocFiles?.has(docType) ||
                formData.requiredDocumentUploads?.some(
                  (u) => u.documentName === docType && u.submitted,
                ),
            )
          const allSigned =
            !onboardingDocs ||
            onboardingDocs.documentsToBeSigned.length === 0 ||
            onboardingDocs.documentsToBeSigned.every(
              (doc) => doc.hasSigned || signedDocFiles?.has(doc.id),
            )
          const isDocsValid = allRequiredUploaded && allSigned
          const docsGradient = isDocsValid
            ? "from-green-500/5 to-green-500/10"
            : "from-primary/5 to-primary/10"
          const docsBorder = isDocsValid
            ? "border-green-200/50 dark:border-green-900/30"
            : "border-primary/20"
          return (
            <div
              className={`bg-linear-to-br ${docsGradient} p-4 rounded-lg border ${docsBorder}`}
            >
              <h3 className="font-semibold text-foreground mb-3 flex items-center text-sm gap-2">
                <FaUpload className="text-primary" />
                Documents Status
              </h3>

              {onboardingDocs &&
                onboardingDocs.requiredDocuments.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-foreground mb-2">
                      Required Documents
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {onboardingDocs.requiredDocuments.map(
                        (docType: string) => {
                          const hasFile =
                            (requiredDocFiles?.has(docType) ?? false) ||
                            (formData.requiredDocumentUploads?.some(
                              (u) => u.documentName === docType && u.submitted,
                            ) ??
                              false)
                          return (
                            <div
                              key={docType}
                              className={`text-center p-2 rounded-lg shadow-sm border ${hasFile ? "bg-card border-transparent" : "bg-destructive/10 border-destructive/20"}`}
                            >
                              {hasFile ? (
                                <FaCheckCircle className="w-5 h-5 mx-auto mb-1 text-green-500" />
                              ) : (
                                <FaExclamationCircle className="w-5 h-5 mx-auto mb-1 text-red-400" />
                              )}
                              <p className="text-xs font-medium text-foreground">
                                {docType}
                              </p>
                              <p
                                className={`text-xs mt-0.5 ${hasFile ? "text-green-600 dark:text-green-400 font-medium" : "text-destructive font-semibold"}`}
                              >
                                {hasFile ? "Selected" : "Missing"}
                              </p>
                            </div>
                          )
                        },
                      )}
                    </div>
                  </div>
                )}

              {onboardingDocs &&
                onboardingDocs.documentsToBeSigned.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-2">
                      Documents To Be Signed
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {onboardingDocs.documentsToBeSigned.map((doc) => {
                        const hasFile =
                          doc.hasSigned ||
                          (signedDocFiles?.has(doc.id) ?? false)
                        return (
                          <div
                            key={doc.id}
                            className={`text-center p-2 rounded-lg shadow-sm border ${hasFile ? "bg-card border-transparent" : "bg-amber-500/10 border-amber-500/30"}`}
                          >
                            {hasFile ? (
                              <FaCheckCircle className="w-5 h-5 mx-auto mb-1 text-green-500" />
                            ) : (
                              <FileText className="w-5 h-5 mx-auto mb-1 text-amber-400" />
                            )}
                            <p className="text-xs font-medium text-foreground truncate">
                              {doc.fileName}
                            </p>
                            <p
                              className={`text-xs mt-0.5 ${hasFile ? "text-green-600 dark:text-green-400 font-medium" : "text-amber-600 dark:text-amber-400 font-semibold"}`}
                            >
                              {doc.approved
                                ? "Approved"
                                : doc.hasSigned
                                  ? "Signed"
                                  : signedDocFiles?.has(doc.id)
                                    ? "Selected"
                                    : "Pending"}
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

              {(!onboardingDocs ||
                (onboardingDocs.requiredDocuments.length === 0 &&
                  onboardingDocs.documentsToBeSigned.length === 0)) && (
                <p className="text-xs text-muted-foreground">No documents required</p>
              )}
            </div>
          )
        })()}

        {response && (
          <div className="mb-4 p-4 rounded-lg border-2 bg-card shadow-sm lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-foreground">
                  {response}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={onClearResponse}
              >
                <FaTimes className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
